import { useState } from 'react';
import { IS_LOCKED } from '../../apiBase';
import { ConfirmDialog } from './ConfirmDialog';
import { MaterialsTable } from './MaterialsTable';
import { ReadingsMenu } from './ReadingsMenu';
import {
  answerOf, appliedIdOf, deleteReading, readsOf, startedOf, useMaterialsRun,
} from './materialsRun';
import { useHardwareRun } from './defaultTexturesRun';
import { useSince } from './waitClock';
import { useLanguage } from '../../contexts/LanguageContext';

type MaterialsSnap = ReturnType<typeof useMaterialsRun>;

// ONE COLOURWAY'S PRODUCT MATERIALS: its readings and the one on screen. One per colourway, all
// on the page at once (see MaterialsPanel) — a beige seat and a grey one are two answers, and
// comparing them is reading down the page. The apply is the panel's, ONE for every colourway.
//
// The standard materials are not here: they are every colourway's, drawn once under these.
// THE READING A COLOURWAY SHOWS: the one picked, else the applied one, else the newest. Shared
// with the panel's one Apply, which applies what each table shows.
export function shownReading(snap: MaterialsSnap, sub: string, picked: string | null | undefined) {
  const reads = readsOf(snap, sub);
  const appliedId = appliedIdOf(snap, sub);
  return reads.find((one) => one.id === picked)
    ?? reads.find((one) => one.id === appliedId)
    ?? reads[0]
    ?? answerOf(snap, sub);
}

export function ColourwayMaterials({ docId, sub, title, locked, pressing, picked, onPick }: {
  docId: string;
  sub: string;
  title: string;
  locked?: boolean;
  // A press is out, for any colourway: its calls write into the newest reading.
  pressing: boolean;
  // WHICH READING IS ON SCREEN, held by the panel: its one Apply writes what every table shows.
  // Looking at a reading is free — comparing two of them is the whole reason both are kept.
  picked: string | null;
  onPick: (id: string | null) => void;
}) {
  const { t } = useLanguage();
  const snap = useMaterialsRun(docId);
  // TWO STAGES OF ONE PRESS: the call, then the measurement off its patches.
  const calling = snap.reading.has(sub);
  const measuring = snap.cropping.has(sub);
  const reading = calling || measuring;
  const applying = snap.applying.has(sub);
  const reads = readsOf(snap, sub);
  // THE READING ASKED ABOUT, before it goes: it was paid for.
  const [asking, setAsking] = useState<string | null>(null);
  const appliedId = appliedIdOf(snap, sub);
  const shown = shownReading(snap, sub, picked);
  const waited = useSince(startedOf(snap, sub));
  const askingHardware = useHardwareRun(docId).busy;

  return (
    <div className="flex flex-col gap-4">
      {/* EVERY READING OF THIS COLOURWAY, as a menu whose face is the one on screen. */}
      {(reads.length > 0 || calling) && (
        <div className="flex justify-center">
          <ReadingsMenu
            reads={reads}
            shownId={shown.id}
            appliedId={appliedId}
            calling={calling}
            elapsed={waited}
            busyId={(measuring || askingHardware) && picked ? picked : applying ? appliedId : undefined}
            onSelect={onPick}
            // NOT THE APPLIED ONE; nor while a press is out, whose calls write into the newest row.
            canDelete={(id) => id !== appliedId && !pressing && !IS_LOCKED && !locked}
            onDelete={setAsking}
          />
        </div>
      )}
      {asking && (
        <ConfirmDialog
          title={t('notice.3d.matDelete')}
          confirmLabel={t('notice.3d.matDeleteGo')}
          cancelLabel={t('notice.3d.matDeleteCancel')}
          onConfirm={() => {
            deleteReading(docId, sub, asking);
            if (picked === asking) onPick(null);
            setAsking(null);
          }}
          onCancel={() => setAsking(null)}
        />
      )}
      <MaterialsTable
        docId={docId} submodel={sub} answer={shown} applied={shown.id === appliedId}
        locked={locked} pending={reading} only="product" title={title}
      />
    </div>
  );
}
